import {
  ApiProperty,
  ApiPropertyOptional,
  IsArray,
  IsBoolean,
  IsEnum,
  IsInt,
  IsOptional,
  IsString,
  Max,
  Min,
  Type,
} from './decorators-nest';
import { RecipeDifficulty, RecipePrice, RecipeTimings, RecipeType } from './recipe.interface';
import { Transform } from 'class-transformer';
import { SpecificDiet } from './food-preference-survey.interface';

/**
 * Normalize a query-string value that may be a single value or a repeated /
 * comma-separated list into a flat array of trimmed strings.
 *
 * NOTE: class-transformer 0.3.x passes the raw value as the first positional
 * argument (not a `{ value }` object like 0.4+), so these transformers take the
 * value directly.
 */
const toStringArray = (value: unknown): string[] | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const raw = Array.isArray(value) ? value : String(value).split(',');
  return raw.map((v) => String(v).trim()).filter((v) => v.length > 0);
};

/** Same as {@link toStringArray} but casts each element to a number (invalid ones become NaN so validation rejects them). */
const toNumberArray = (value: unknown): number[] | undefined => {
  const arr = toStringArray(value);
  return arr?.map((v) => Number(v));
};

/** Parse the common truthy string representations coming from a query string. */
const toBoolean = (value: unknown): boolean | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'boolean') return value;
  return ['true', '1', 'yes'].includes(String(value).toLowerCase());
};

/**
 * Query parameters for the mobile recipe catalog list.
 *
 * Preference filters (diet / food exclusions / meal types derived from the
 * patient's latest food_preference_survey) are applied by default and can be
 * turned off with `applyPreferences=false`. Every other filter is ANDed on top.
 */
export class RecipeCatalogQueryDto {
  @IsOptional()
  @IsString()
  @ApiPropertyOptional({ description: 'Free text search on the recipe name (accent/case insensitive)', example: 'quiche' })
  search?: string;

  @IsOptional()
  @IsArray()
  @IsEnum(RecipeType, { each: true })
  @Transform(toStringArray)
  @ApiPropertyOptional({ isArray: true, enum: RecipeType, description: 'Restrict to these meal types' })
  types?: RecipeType[];

  @IsOptional()
  @IsArray()
  @IsInt({ each: true })
  @Transform(toNumberArray)
  @ApiPropertyOptional({ isArray: true, type: Number, description: 'Recipe must carry ALL of these tag ids', example: [67] })
  tagIds?: number[];

  @IsOptional()
  @IsArray()
  @IsInt({ each: true })
  @Transform(toNumberArray)
  @ApiPropertyOptional({ isArray: true, type: Number, description: 'Exclude recipes carrying any of these tag ids' })
  excludeTagIds?: number[];

  @IsOptional()
  @IsBoolean()
  @Transform(toBoolean)
  @ApiPropertyOptional({ default: true, description: 'Apply the patient preference filters (diet, exclusions, meal types)' })
  applyPreferences?: boolean = true;

  @IsOptional()
  @IsString()
  @ApiPropertyOptional({ description: 'Opaque keyset cursor returned as `nextCursor` by the previous page' })
  cursor?: string;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(50)
  @ApiPropertyOptional({ default: 20, minimum: 1, maximum: 50 })
  pageSize?: number = 20;
}

/** Lean recipe representation returned in the catalog list (no ingredients/steps/nutrients). */
export class RecipeCatalogTagDto {
  @ApiProperty({ example: 67 })
  id: number;

  @ApiProperty({ example: 'Végétarien' })
  name: string;
}

/**
 * One "régime en particulier" filter option (single-select facet in the app).
 *
 * A specific diet maps to a single recipe tag applied either as an inclusion
 * (`require` → send as `tagIds`) or an exclusion (`exclude` → send as
 * `excludeTagIds`) on `GET /recipes/catalog`.
 */
export class RecipeCatalogDietDto {
  @ApiProperty({ enum: SpecificDiet, example: SpecificDiet.VEGETARIAN })
  diet: SpecificDiet;

  @ApiProperty({ example: 'Végétarien', description: 'Label to display on the chip' })
  label: string;

  @ApiProperty({
    example: 67,
    description: 'Tag id to send — as `tagIds` when mode=require, as `excludeTagIds` when mode=exclude',
  })
  tagId: number;

  @ApiProperty({
    enum: ['require', 'exclude'],
    example: 'require',
    description: 'Whether the recipe must carry (require) or must not carry (exclude) the tag',
  })
  mode: 'require' | 'exclude';
}

export class RecipeCatalogItemDto {
  @ApiProperty()
  id: number;

  @ApiProperty()
  name: string;

  @ApiProperty({ enum: RecipeType, nullable: true })
  type: RecipeType;

  @ApiProperty({ enum: RecipePrice, nullable: true })
  price: RecipePrice;

  @ApiProperty({ enum: RecipeDifficulty })
  difficulty: RecipeDifficulty;

  @ApiProperty({ nullable: true })
  caloriesPerServing: number;

  @ApiProperty({ nullable: true })
  imageUrl: string;

  @ApiProperty({ type: () => RecipeTimings })
  timings: RecipeTimings;

  @ApiProperty({ type: () => RecipeCatalogTagDto, isArray: true })
  tags: RecipeCatalogTagDto[];
}

/** One page of the catalog. `total` is only computed on the first page (no cursor). */
export class RecipeCatalogPageDto {
  @ApiProperty({ type: () => RecipeCatalogItemDto, isArray: true })
  items: RecipeCatalogItemDto[];

  @ApiProperty({ nullable: true, description: 'Cursor to fetch the next page, or null when this is the last page' })
  nextCursor: string | null;

  @ApiPropertyOptional({ nullable: true, description: 'Total number of matching recipes (first page only)' })
  total?: number | null;
}

/**
 * Facets and preference-derived default filters for the catalog UI.
 * The app pre-fills its filter controls from this payload then always sends
 * explicit filters to `GET /recipes/catalog`.
 */
export class RecipeCatalogFiltersDto {
  @ApiProperty({ description: 'Whether the patient has a food_preference_survey to derive defaults from' })
  hasPreferences: boolean;

  @ApiProperty({ enum: SpecificDiet, nullable: true })
  diet: SpecificDiet | null;

  @ApiProperty({ isArray: true, enum: RecipeType, description: 'Meal types the patient consumes (default selection)' })
  defaultTypes: RecipeType[];

  @ApiProperty({ isArray: true, type: Number, description: 'Ingredient ids excluded by the patient' })
  excludedIngredientIds: number[];

  @ApiProperty({ isArray: true, type: Number, description: 'Ingredient-tag ids excluded by the patient' })
  excludedTagIds: number[];

  @ApiProperty({ isArray: true, enum: RecipeType, description: 'All meal types available in the published catalog' })
  availableTypes: RecipeType[];

  @ApiProperty({ type: () => RecipeCatalogTagDto, isArray: true, description: 'All tags used by the published catalog' })
  availableTags: RecipeCatalogTagDto[];

  @ApiProperty({
    type: () => RecipeCatalogDietDto,
    isArray: true,
    description: '« Régime en particulier » facet: specific-diet options present in the published catalog (single-select)',
  })
  diets: RecipeCatalogDietDto[];

  @ApiProperty({
    type: () => RecipeCatalogTagDto,
    isArray: true,
    description:
      '« Profil profertilité » facet: profertility profile tags (endométriose, SOPK, +35 ans, anti-inflammatoire) present in the catalog (multi-select via tagIds)',
  })
  profertilityTags: RecipeCatalogTagDto[];
}
